import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { settingsApi } from '../api/settings';
import { createV2BrandTheme as createAppTheme } from '../theme/v2BrandTheme';
import type { TenantBranding } from '../types/branding';
import { useAuth } from '../auth/AuthContext';

interface BrandingContextValue {
  branding: TenantBranding | null;
  /** Customer/organization display name (from a tenant setting), shown in the header. */
  organizationName: string | null;
  isLoading: boolean;
}

const BrandingContext = createContext<BrandingContextValue>({
  branding: null,
  organizationName: null,
  isLoading: false,
});

export function useBranding(): BrandingContextValue {
  return useContext(BrandingContext);
}

/** Tenant setting keys (case-insensitive) that may carry the customer name. */
const ORG_NAME_KEYS = ['organizationname', 'customername', 'companyname', 'organization', 'tenantname'];

interface Props {
  children: ReactNode;
}

export function BrandingProvider({ children }: Props): JSX.Element {
  const { isAuthenticated } = useAuth();
  const query = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });

  const branding = query.data?.branding ?? null;
  const organizationName = useMemo(() => {
    const settings = query.data?.settings ?? [];
    const match = settings.find(
      (s) => ORG_NAME_KEYS.includes(s.key.toLowerCase()) && s.value && s.value.trim().length > 0,
    );
    return match?.value?.trim() ?? null;
  }, [query.data?.settings]);

  const theme = useMemo(
    () => createAppTheme(branding?.primaryColor, branding?.secondaryColor),
    [branding?.primaryColor, branding?.secondaryColor],
  );

  return (
    <BrandingContext.Provider value={{ branding, organizationName, isLoading: query.isLoading }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </BrandingContext.Provider>
  );
}
